import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Mail, Calendar } from 'lucide-react';
import type { Quote, Client } from '@/lib/api';
import { formatAmount } from '@/lib/currency';

// Format date as YYYY-MM-DD for input value
function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Get default due date (30 days from now)
function getDefaultDueDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return formatDateForInput(date);
}

interface ConvertToInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Quote;
  client: Client | null;
  onConvert: (options: { sendEmail: boolean; dueDate: string }) => Promise<void>;
}

export function ConvertToInvoiceModal({
  open,
  onOpenChange,
  quote,
  client,
  onConvert,
}: ConvertToInvoiceModalProps) {
  const [sendEmail, setSendEmail] = useState(false);
  const [converting, setConverting] = useState(false);
  const [dueDate, setDueDate] = useState(getDefaultDueDate());

  const hasEmail = client?.emails && client.emails.length > 0;
  const clientEmail = hasEmail ? client.emails[0] : null;

  const handleConvert = async () => {
    setConverting(true);
    try {
      await onConvert({ sendEmail, dueDate });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to convert quote:', error);
    } finally {
      setConverting(false);
    }
  };

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSendEmail(false);
      setDueDate(getDefaultDueDate());
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Convert to Invoice</DialogTitle>
          <DialogDescription>
            This will mark the quote as converted and generate an invoice from it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Client Preview Card */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-semibold text-sm">
                  {client?.billing_name?.substring(0, 2).toUpperCase() || 'CL'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground truncate">
                  {client?.billing_name || 'Unknown Client'}
                </p>
                {client?.company_name && (
                  <p className="text-sm text-muted-foreground truncate">
                    {client.company_name}
                  </p>
                )}
              </div>
            </div>

            {clientEmail && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{clientEmail}</span>
              </div>
            )}
          </div>

          {/* Amount Section */}
          <div className="flex justify-between items-center py-4 border-t border-b">
            <div>
              <p className="text-sm text-muted-foreground">Invoice Amount</p>
              <p className="text-sm text-muted-foreground">From Quote #{quote.quote_number}</p>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {formatAmount(quote.total_amount)}
            </p>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="due-date" className="text-sm font-medium">
              Invoice Due Date
            </Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={formatDateForInput(new Date())}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              When payment is due for this invoice
            </p>
          </div>

          {/* Email Checkbox - only if client has email */}
          {hasEmail && (
            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="send-email"
                checked={sendEmail}
                onCheckedChange={(checked) => setSendEmail(checked === true)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="send-email"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Email the invoice to the client
                </Label>
                <p className="text-xs text-muted-foreground">{clientEmail}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={converting}
          >
            Cancel
          </Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={handleConvert}
            disabled={converting}
          >
            {converting ? 'Converting...' : 'Convert to Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
